// A project's sync connection (PROTOCOL.md §5): frames over a WebSocket, blob uploads and pack downloads.
import { decodeFrame, encodeFrame, MISSING, type ClientMessage, type ServerMessage, type WireEntry } from '../shared/wire.ts';
import { addressPath } from './address.ts';
import type { Api } from './api.ts';

const REPLY_TIMEOUT_MS = 60_000;

export type Welcome = Extract<ServerMessage, { t: 'welcome' }>;
export type PresenceHeader = Extract<ServerMessage, { t: 'presence' }>;
type Pending = { resolve: (v: { header: ServerMessage; body: Uint8Array }) => void; reject: (e: Error) => void };

export interface Loaded {
  entries: WireEntry[];
  /** Inline bytes by seq. */
  inline: Map<number, Uint8Array>;
  head: number;
  reset: boolean;
  limits: Welcome['limits'];
}

export interface AppendFields {
  doc: string;
  epoch: number;
  lineage: string;
  cid: string;
  heads?: string;
  base?: number;
  blob?: { key: string; size: number };
  /** The fingerprint of the key the bytes are sealed with (PROTOCOL.md §10): an old key's are refused (`keyChanged`). */
  keyFingerprint?: string;
}

export const newCid = () => crypto.randomUUID().replaceAll('-', '');

export class SyncConnection {
  private ws!: WebSocket;
  private nextID = 1;
  private pending = new Map<number, Pending>();
  closed = false;
  closeReason = '';
  /** Another device appended an entry (bytes inline when small). */
  onNew: ((entry: WireEntry, bytes: Uint8Array | null) => void) | null = null;
  /** Ask for other members' presence in `hello` (PROTOCOL.md §10.12); set before `hello`. */
  presence = false;
  /** Another member's presence frame (its sealed payload as the body; none for `gone`). */
  onPresence: ((header: PresenceHeader, body: Uint8Array) => void) | null = null;
  onClose: ((code: number, reason: string) => void) | null = null;

  constructor(
    readonly api: Api,
    /** The project's address (address.ts): its id, or `<owner>/<id>` for a project shared with this account. */
    readonly project: string,
  ) {}

  async connect(): Promise<void> {
    const token = await this.api.access();
    const url = `${this.api.origin.replace(/^http/, 'ws')}/v1/projects/${addressPath(this.project)}/socket`;
    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(url, ['relay.v1', `bearer.${token}`]);
      ws.binaryType = 'arraybuffer';
      ws.onopen = () => resolve();
      ws.onerror = () => reject(new Error(`couldn’t connect to ${url}`));
      ws.onclose = (event) => {
        this.closed = true;
        this.closeReason = `code ${event.code}${event.reason ? `, ${event.reason}` : ''}`;
        for (const p of this.pending.values()) p.reject(new Error(`socket closed (${this.closeReason})`));
        this.pending.clear();
        this.onClose?.(event.code, event.reason);
      };
      ws.onmessage = (event) => {
        if (typeof event.data === 'string') return; // 'pong'
        const frame = decodeFrame<ServerMessage>(event.data as ArrayBuffer);
        const h = frame.header;
        if (h.t === 'presence') {
          this.onPresence?.(h, frame.body);
          return;
        }
        if (h.t === 'new') {
          const bytes = h.entry.off !== undefined ? frame.body.subarray(h.entry.off, h.entry.off + h.entry.size) : null;
          this.onNew?.(h.entry, bytes);
          return;
        }
        const id = (h as { id?: number }).id ?? 0;
        const p = this.pending.get(id);
        if (p) {
          this.pending.delete(id);
          p.resolve(frame);
        }
      };
      this.ws = ws;
    });
  }

  close() {
    this.ws?.close();
    this.closed = true;
  }

  /** Keeps idle sockets alive through proxies; the relay answers without waking the project's object. */
  ping() {
    if (!this.closed) this.ws.send('ping');
  }

  private request(msg: Omit<ClientMessage, 'id'>, body?: Uint8Array): Promise<{ header: ServerMessage; body: Uint8Array }> {
    // A closed socket drops what's sent on it without an error: fail here rather than wait.
    if (this.closed || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error(`socket closed before ${msg.t} (${this.closeReason || `state ${this.ws?.readyState}`})`));
    }
    const id = this.nextID++;
    const frame = encodeFrame({ ...msg, id }, body);
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`no reply to ${msg.t} in ${REPLY_TIMEOUT_MS / 1000} s`));
      }, REPLY_TIMEOUT_MS);
      this.pending.set(id, {
        resolve: (v) => (clearTimeout(timer), resolve(v)),
        reject: (e) => (clearTimeout(timer), reject(e)),
      });
      this.ws.send(frame);
    });
  }

  /** Sends a presence frame (§10.12): the sealed payload, or `gone` with none. Never answered; false when the socket
   *  isn't open (presence is only ever the latest state, so nothing is kept to send later). */
  sendPresence(o: { ttl: number; sealed: Uint8Array } | { gone: true }): boolean {
    if (this.closed || this.ws?.readyState !== WebSocket.OPEN) return false;
    try {
      this.ws.send('gone' in o ? encodeFrame({ t: 'presence', gone: true }) : encodeFrame({ t: 'presence', ttl: o.ttl }, o.sealed));
      return true;
    } catch {
      return false;
    }
  }

  /** Everything after `cursor`, following pages. */
  async hello(cursor: number): Promise<Loaded> {
    const out: Loaded = { entries: [], inline: new Map(), head: 0, reset: false, limits: { inlineMax: 0, snapshotInlineMax: 0 } };
    for (;;) {
      const { header, body } = await this.request({ t: 'hello', cursor, ...(this.presence ? { presence: true } : {}) } as Omit<ClientMessage, 'id'>);
      if (header.t === 'error') throw new Error(`${header.code}: ${header.message}`);
      const w = header as Welcome;
      for (const e of w.entries) {
        out.entries.push(e);
        if (e.off !== undefined) out.inline.set(e.seq, body.slice(e.off, e.off + e.size));
      }
      out.head = w.head;
      out.limits = w.limits;
      out.reset ||= !!w.reset;
      cursor = w.next;
      if (!w.more) return out;
    }
  }

  /** Appends a sealed snapshot or batch, uploading it as a blob first when it's over the inline limit. */
  async append(kind: 'push' | 'snapshot', fields: AppendFields, sealed: Uint8Array, limits: Welcome['limits']): Promise<Extract<ServerMessage, { t: 'ack' | 'error' }>> {
    const limit = kind === 'snapshot' ? limits.snapshotInlineMax : limits.inlineMax;
    if (sealed.length > limit && !fields.blob) {
      const blob = await this.uploadBlob(fields.doc, fields.epoch, fields.cid, sealed);
      return (await this.request({ t: kind, ...fields, blob } as Omit<ClientMessage, 'id'>)).header as Extract<ServerMessage, { t: 'ack' | 'error' }>;
    }
    return (await this.request({ t: kind, ...fields } as Omit<ClientMessage, 'id'>, sealed)).header as Extract<ServerMessage, { t: 'ack' | 'error' }>;
  }

  private async http(path: string, init: RequestInit = {}): Promise<Response> {
    const res = await fetch(`${this.api.origin}/v1/projects/${addressPath(this.project)}${path}`, {
      ...init,
      headers: { ...(init.headers as Record<string, string>), authorization: `Bearer ${await this.api.access()}` },
    });
    if (!res.ok && res.status !== 404) throw new Error(`${path} ${res.status} ${await res.text()}`);
    return res;
  }

  async uploadBlob(doc: string, epoch: number, cid: string, bytes: Uint8Array): Promise<{ key: string; size: number }> {
    return (await this.http(`/blobs/${doc}/${epoch}/${cid}`, { method: 'PUT', body: bytes as BodyInit })).json();
  }

  /** Blobs by key, fetched through the relay's pack endpoint (≤ 500 per call). */
  async pack(keys: string[]): Promise<Map<string, Uint8Array>> {
    const out = new Map<string, Uint8Array>();
    for (let i = 0; i < keys.length; i += 500) {
      const chunk = keys.slice(i, i + 500);
      const res = await this.http('/pack', { method: 'POST', body: JSON.stringify({ keys: chunk }), headers: { 'content-type': 'application/json' } });
      const all = new Uint8Array(await res.arrayBuffer());
      const view = new DataView(all.buffer, all.byteOffset, all.byteLength);
      for (let at = 0; at < all.length; ) {
        const index = view.getUint32(at);
        const len = view.getUint32(at + 4);
        at += 8;
        if (len === MISSING) continue;
        out.set(chunk[index], all.subarray(at, at + len));
        at += len;
      }
    }
    return out;
  }
}
